import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  writeBatch,
  Timestamp,
  deleteDoc,
  setDoc
} from 'firebase/firestore';
import { 
  db, 
  auth 
} from '../services/firebase';
import { 
  createUserWithEmailAndPassword,
  deleteUser,
  signInWithEmailAndPassword 
} from 'firebase/auth';

const COLLECTIONS = {
  LEADS: 'leads',
  AGENTS: 'agents',
  USERS: 'users'
};

export const adminService = {
  // ✅ Get all leads with proper error handling
  getAllLeads: async () => {
    try {
      console.log('🔍 Fetching all leads from Firestore...');
      
      const q = query(
        collection(db, COLLECTIONS.LEADS), 
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const leads = [];
      querySnapshot.forEach((doc) => {
        leads.push({ 
          id: doc.id, 
          ...doc.data() 
        });
      });
      
      console.log(`✅ Successfully fetched ${leads.length} leads`);
      return leads;
    } catch (error) {
      console.error('❌ Error getting all leads:', error);
      throw new Error('Failed to load leads: ' + error.message);
    }
  },

  // ✅ Get all agents with proper structure
  getAllAgents: async () => {
    try {
      console.log('🔍 Fetching all agents from Firestore...');
      
      const q = query(collection(db, COLLECTIONS.AGENTS));
      const querySnapshot = await getDocs(q);
      
      const agents = [];
      querySnapshot.forEach((doc) => {
        const agentData = doc.data();
        agents.push({ 
          id: doc.id, 
          ...agentData 
        });
      });
      
      console.log(`✅ Successfully fetched ${agents.length} agents`);
      return agents;
    } catch (error) {
      console.error('❌ Error getting all agents:', error);
      throw new Error('Failed to load agents: ' + error.message);
    }
  },

  // ✅ FIXED: Create agent without logging out admin
  createAgent: async (agentData) => {
    try {
      const { email, password, firstName, lastName, phone, role } = agentData;
      const currentAdmin = auth.currentUser;

      if (!currentAdmin) {
        throw new Error('Admin authentication required');
      }

      console.log('👤 Creating new agent account:', email);

      // Store admin credentials temporarily
      const adminEmail = currentAdmin.email;
      
      // Create the new user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;

      console.log('✅ Firebase auth user created:', newUser.uid);

      // Prepare agent profile data
      const agentProfile = {
        uid: newUser.uid,
        email: email,
        firstName: firstName,
        lastName: lastName,
        phone: phone || '',
        role: role || 'agent',
        blocked: false,
        status: 'active',
        createdAt: serverTimestamp(),
        createdBy: adminEmail,
        lastLogin: null,
        displayName: `${firstName} ${lastName}`
      };

      // Save to Firestore agents collection
      const agentRef = doc(db, COLLECTIONS.AGENTS, newUser.uid);
      await setDoc(agentRef, agentProfile);

      console.log('✅ Agent profile saved to Firestore');

      // Also create a user profile for consistency
      const userProfile = {
        uid: newUser.uid,
        email: email,
        firstName: firstName,
        lastName: lastName,
        role: role || 'agent',
        createdAt: serverTimestamp(),
        status: 'active'
      };

      const userRef = doc(db, COLLECTIONS.USERS, newUser.uid);
      await setDoc(userRef, userProfile);

      console.log('✅ User profile saved to Firestore');

      // 🔄 CRITICAL FIX: Sign back in the original admin
      await signInWithEmailAndPassword(auth, adminEmail, prompt('Please enter your admin password to continue:'));

      console.log('✅ Admin session restored');

      return {
        success: true,
        agent: agentProfile,
        message: `${role === 'admin' ? 'Admin' : 'Agent'} created successfully`
      };

    } catch (error) {
      console.error('❌ Error creating agent:', error);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to create agent account';
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'This email address is already registered';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password should be at least 6 characters';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Email/password accounts are not enabled';
          break;
        default:
          errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  },

  // ✅ FIXED: Delete agent properly
  deleteAgent: async (agentId) => {
    try {
      console.log('🗑️ Deleting agent:', agentId);

      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      // First, get the agent document to check if it exists
      const agentRef = doc(db, COLLECTIONS.AGENTS, agentId);
      const agentSnap = await getDoc(agentRef);

      if (!agentSnap.exists()) {
        throw new Error(`Agent document not found: ${agentId}`);
      }

      const agentData = agentSnap.data();
      
      // Delete the agent document from Firestore
      await deleteDoc(agentRef);
      console.log('✅ Agent document deleted from Firestore');

      // Try to delete the auth user (requires admin privileges)
      try {
        // Note: This requires Firebase Admin SDK on backend or special privileges
        // For now, we'll just mark the user as deleted in Firestore
        console.log('⚠️ Auth user deletion requires backend implementation');
        
        // Update the user profile to mark as deleted
        const userRef = doc(db, COLLECTIONS.USERS, agentId);
        await updateDoc(userRef, {
          deleted: true,
          deletedAt: serverTimestamp(),
          status: 'deleted'
        });
        
      } catch (authError) {
        console.warn('Could not delete auth user:', authError.message);
        // Continue with Firestore deletion even if auth deletion fails
      }

      // Reassign or delete leads assigned to this agent
      await adminService.reassignAgentLeads(agentId);

      return { 
        success: true, 
        message: 'Agent deleted successfully' 
      };

    } catch (error) {
      console.error('❌ Error deleting agent:', error);
      throw new Error('Failed to delete agent: ' + error.message);
    }
  },

  // ✅ Reassign leads when agent is deleted
  reassignAgentLeads: async (agentId) => {
    try {
      const q = query(
        collection(db, COLLECTIONS.LEADS),
        where('assignedAgentId', '==', agentId)
      );
      
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);
      let reassignedCount = 0;

      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, {
          assignedAgentId: '',
          assignedAgentEmail: '',
          assignedAgentName: '',
          status: 'New',
          updatedAt: serverTimestamp(),
          notes: `Previous agent deleted. ${doc.data().notes || ''}`
        });
        reassignedCount++;
      });

      if (reassignedCount > 0) {
        await batch.commit();
        console.log(`✅ Reassigned ${reassignedCount} leads from deleted agent`);
      }

      return reassignedCount;
    } catch (error) {
      console.error('Error reassigning leads:', error);
      throw error;
    }
  },

  // ✅ Block/Unblock agent
  blockUnblockAgent: async (agentId, block) => {
    try {
      console.log(`🔒 ${block ? 'Blocking' : 'Unblocking'} agent:`, agentId);

      // First check if agent exists in agents collection
      const agentRef = doc(db, COLLECTIONS.AGENTS, agentId);
      const agentSnap = await getDoc(agentRef);

      if (!agentSnap.exists()) {
        console.warn(`Agent ${agentId} not found in agents collection, trying users collection`);
        
        // Try users collection as fallback
        const userRef = doc(db, COLLECTIONS.USERS, agentId);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          throw new Error('Agent/User not found in database');
        }
        
        // Update user document
        await updateDoc(userRef, {
          blocked: block,
          status: block ? 'blocked' : 'active',
          updatedAt: serverTimestamp()
        });
        
        console.log(`✅ User ${block ? 'blocked' : 'unblocked'} successfully in users collection`);
        return { success: true };
      }

      // Update agent document
      await updateDoc(agentRef, {
        blocked: block,
        status: block ? 'blocked' : 'active',
        updatedAt: serverTimestamp()
      });

      // Also update user profile if it exists
      try {
        const userRef = doc(db, COLLECTIONS.USERS, agentId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          await updateDoc(userRef, {
            blocked: block,
            status: block ? 'blocked' : 'active',
            updatedAt: serverTimestamp()
          });
        }
      } catch (userError) {
        console.warn('Could not update user document:', userError.message);
      }

      console.log(`✅ Agent ${block ? 'blocked' : 'unblocked'} successfully`);
      return { success: true };

    } catch (error) {
      console.error('❌ Error updating agent block status:', error);
      throw new Error('Failed to update agent: ' + error.message);
    }
  },

  // ✅ Enhanced dashboard stats
  getDashboardStats: async () => {
    try {
      console.log('📊 Fetching dashboard statistics...');

      const [leadsSnapshot, agentsSnapshot] = await Promise.all([
        getDocs(collection(db, COLLECTIONS.LEADS)),
        getDocs(collection(db, COLLECTIONS.AGENTS))
      ]);

      const leads = [];
      const agents = [];
      
      leadsSnapshot.forEach(doc => {
        leads.push({ id: doc.id, ...doc.data() });
      });
      
      agentsSnapshot.forEach(doc => {
        agents.push({ id: doc.id, ...doc.data() });
      });

      // Calculate metrics
      const assignedLeads = leads.filter(lead => lead.assignedAgentId && lead.assignedAgentId !== '').length;
      const unassignedLeads = leads.filter(lead => !lead.assignedAgentId || lead.assignedAgentId === '').length;
      
      // Recent leads (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const recentLeads = leads.filter(lead => {
        try {
          const leadDate = lead.createdAt?.toDate?.() || new Date(lead.createdAt);
          return leadDate >= sevenDaysAgo;
        } catch {
          return false;
        }
      }).length;

      // Blocked agents
      const blockedAgents = agents.filter(agent => agent.blocked === true).length;

      // Leads by status
      const leadsByStatus = {};
      leads.forEach(lead => {
        const status = lead.status || 'Unknown';
        leadsByStatus[status] = (leadsByStatus[status] || 0) + 1;
      });

      const stats = {
        totalLeads: leads.length,
        totalAgents: agents.length,
        assignedLeads,
        unassignedLeads,
        recentLeads,
        blockedAgents,
        activeAgents: agents.length - blockedAgents,
        leadsByStatus
      };

      console.log('✅ Dashboard stats calculated:', stats);
      return stats;

    } catch (error) {
      console.error('❌ Error getting dashboard stats:', error);
      throw new Error('Failed to load dashboard statistics: ' + error.message);
    }
  },

  // ✅ Import lead functionality
  importLead: async (leadData, assignToAgent = null) => {
    try {
      let assignedAgent = null;
      
      // If agent email is provided, find the agent
      if (assignToAgent) {
        const agentsQuery = query(
          collection(db, COLLECTIONS.AGENTS), 
          where('email', '==', assignToAgent)
        );
        const agentsSnapshot = await getDocs(agentsQuery);
        
        if (!agentsSnapshot.empty) {
          const agentDoc = agentsSnapshot.docs[0];
          assignedAgent = {
            id: agentDoc.id,
            ...agentDoc.data()
          };
        }
      }

      const leadWithMetadata = {
        ...leadData,
        assignedAgentId: assignedAgent?.id || '',
        assignedAgentEmail: assignedAgent?.email || '',
        assignedAgentName: assignedAgent ? `${assignedAgent.firstName} ${assignedAgent.lastName}` : '',
        status: 'New',
        source: leadData.source || 'Import',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        followUps: [],
        isImported: true,
        importDate: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.LEADS), leadWithMetadata);
      
      console.log('✅ Lead imported successfully:', docRef.id);
      return {
        id: docRef.id,
        ...leadWithMetadata
      };

    } catch (error) {
      console.error('❌ Error importing lead:', error);
      throw new Error('Failed to import lead: ' + error.message);
    }
  },

  // ✅ Bulk assign leads
  bulkAssignLeads: async (leadIds, agentEmail) => {
    try {
      console.log(`🔄 Bulk assigning ${leadIds.length} leads to:`, agentEmail);

      // Find the agent
      const agentsQuery = query(
        collection(db, COLLECTIONS.AGENTS), 
        where('email', '==', agentEmail)
      );
      const agentsSnapshot = await getDocs(agentsQuery);
      
      if (agentsSnapshot.empty) {
        throw new Error('Agent not found');
      }

      const agentDoc = agentsSnapshot.docs[0];
      const agent = { id: agentDoc.id, ...agentDoc.data() };

      // Update leads in batch
      const batch = writeBatch(db);
      const results = {
        success: 0,
        failed: 0,
        errors: []
      };

      for (const leadId of leadIds) {
        try {
          const leadRef = doc(db, COLLECTIONS.LEADS, leadId);
          const leadSnap = await getDoc(leadRef);
          
          if (leadSnap.exists()) {
            batch.update(leadRef, {
              assignedAgentId: agent.id,
              assignedAgentEmail: agent.email,
              assignedAgentName: `${agent.firstName} ${agent.lastName}`,
              updatedAt: serverTimestamp(),
              lastAssignedAt: serverTimestamp()
            });
            results.success++;
          } else {
            results.failed++;
            results.errors.push(`Lead ${leadId}: Not found`);
          }
        } catch (error) {
          results.failed++;
          results.errors.push(`Lead ${leadId}: ${error.message}`);
        }
      }

      if (results.success > 0) {
        await batch.commit();
        console.log(`✅ Successfully assigned ${results.success} leads`);
      }

      return results;

    } catch (error) {
      console.error('❌ Error in bulk assign:', error);
      throw new Error('Failed to assign leads: ' + error.message);
    }
  },

  // ✅ Update lead status
  updateLeadStatus: async (leadId, newStatus) => {
    try {
      const leadRef = doc(db, COLLECTIONS.LEADS, leadId);
      await updateDoc(leadRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      return { success: true, message: 'Status updated successfully' };
    } catch (error) {
      console.error('❌ Error updating lead status:', error);
      throw new Error('Failed to update status: ' + error.message);
    }
  },

  // ✅ Assign lead to agent
  assignLeadToAgent: async (leadId, agentEmail) => {
    try {
      // Find agent by email
      const agentsQuery = query(
        collection(db, COLLECTIONS.AGENTS), 
        where('email', '==', agentEmail)
      );
      const agentsSnapshot = await getDocs(agentsQuery);
      
      if (agentsSnapshot.empty) {
        throw new Error('Agent not found');
      }

      const agentDoc = agentsSnapshot.docs[0];
      const agent = { id: agentDoc.id, ...agentDoc.data() };

      const leadRef = doc(db, COLLECTIONS.LEADS, leadId);
      await updateDoc(leadRef, {
        assignedAgentId: agent.id,
        assignedAgentEmail: agent.email,
        assignedAgentName: `${agent.firstName} ${agent.lastName}`,
        updatedAt: serverTimestamp(),
        assignedAt: serverTimestamp()
      });

      return { success: true, message: 'Lead assigned successfully' };
    } catch (error) {
      console.error('❌ Error assigning lead to agent:', error);
      throw new Error('Failed to assign lead: ' + error.message);
    }
  },

  // ✅ Delete lead
  deleteLead: async (leadId) => {
    try {
      console.log('🗑️ Deleting lead:', leadId);

      if (!leadId) {
        throw new Error('Lead ID is required');
      }

      const leadRef = doc(db, COLLECTIONS.LEADS, leadId);
      const leadSnap = await getDoc(leadRef);

      if (!leadSnap.exists()) {
        throw new Error('Lead not found');
      }

      // Soft delete by updating status
      await updateDoc(leadRef, {
        deleted: true,
        deletedAt: serverTimestamp(),
        status: 'deleted',
        updatedAt: serverTimestamp()
      });

      console.log('✅ Lead marked as deleted');
      return { 
        success: true, 
        message: 'Lead deleted successfully',
        leadId: leadId // Explicitly return leadId
      };

    } catch (error) {
      console.error('❌ Error deleting lead:', error);
      throw new Error('Failed to delete lead: ' + error.message);
    }
  },


  // ✅ Get lead by ID
  getLeadById: async (leadId) => {
    try {
      const docRef = doc(db, COLLECTIONS.LEADS, leadId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Lead not found');
      }
    } catch (error) {
      console.error('❌ Error getting lead:', error);
      throw new Error('Failed to load lead: ' + error.message);
    }
  },

  // ✅ Get all follow-ups
  getAllFollowUps: async () => {
    try {
      console.log('📋 Fetching all follow-ups...');
      
      const leadsSnapshot = await getDocs(collection(db, COLLECTIONS.LEADS));
      const allFollowUps = [];

      leadsSnapshot.forEach(leadDoc => {
        const leadData = leadDoc.data();
        const leadId = leadDoc.id;
        
        // Check if followUps exist and is an array
        if (leadData.followUps && Array.isArray(leadData.followUps)) {
          leadData.followUps.forEach((followUp, index) => {
            // Ensure follow-up has basic data
            if (followUp && typeof followUp === 'object') {
              allFollowUps.push({
                id: `${leadId}_${followUp.id || index}_${Date.now()}`,
                leadId: leadId,
                leadName: leadData.fullName || 'Unknown Lead',
                leadPhone: leadData.mobileNo || leadData.phone || '',
                leadEmail: leadData.email || '',
                leadStatus: leadData.status || 'Unknown',
                agentEmail: leadData.assignedAgentEmail || 'Unassigned',
                agentName: leadData.assignedAgentName || '',
                date: followUp.date || null,
                notes: followUp.notes || '',
                status: followUp.status || 'scheduled',
                type: followUp.type || 'call',
                createdAt: followUp.createdAt || leadData.createdAt,
                updatedAt: followUp.updatedAt || leadData.updatedAt
              });
            }
          });
        }
      });

      // Sort by date if available
      allFollowUps.sort((a, b) => {
        const dateA = a.date?.toDate?.() || new Date(a.date || 0);
        const dateB = b.date?.toDate?.() || new Date(b.date || 0);
        return dateB - dateA;
      });

      console.log(`✅ Found ${allFollowUps.length} follow-ups`);
      return allFollowUps;

    } catch (error) {
      console.error('❌ Error getting all follow-ups:', error);
      throw new Error('Failed to load follow-ups: ' + error.message);
    }
  },

  // ✅ Get agent by ID
  getAgentById: async (agentId) => {
    try {
      const docRef = doc(db, COLLECTIONS.AGENTS, agentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Agent not found');
      }
    } catch (error) {
      console.error('❌ Error getting agent:', error);
      throw new Error('Failed to load agent: ' + error.message);
    }
  }
};